import {useMutation} from '@tanstack/react-query';
import {apiClient} from '../http/query-client';
import {useTrans} from '@ui/i18n/use-trans';
import {BackendResponse} from '../http/backend-response/backend-response';
import {toast} from '@ui/toast/toast';
import {message} from '@ui/i18n/message';
import {UseFormReturn} from 'react-hook-form';
import {onFormQueryError} from '../errors/on-form-query-error';
import {useNavigate} from '../ui/navigation/use-navigate';
import {getFbc, getFbp} from '../facebook/facebook-track';

interface Response extends BackendResponse {}

export interface ContactPagePayload {
  name: string;
  email: string;
  message: string;
  mobileNumber: string;
}

export function useSubmitContactForm(form: UseFormReturn<ContactPagePayload>) {
  const {trans} = useTrans();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (props: ContactPagePayload) => submitContactForm(props),
    onSuccess: (response, payload) => {
      const contactEventId = 'contact_' + Math.random().toString(36).substring(2, 15);
      
      const parts = (payload.name || '').trim().split(' ');
      const firstName = parts[0] || '';
      const lastName = parts.length > 1 ? parts.slice(1).join(' ') : parts[0] || '';

      const enrichedUserData = {
        em: payload.email,
        ph: payload.mobileNumber,
        fn: firstName,
        ln: lastName,
        fbc: getFbc() || undefined,
        fbp: getFbp() || undefined,
      };

      const hasFbq = typeof window !== 'undefined' && (window as any).fbq;
      if (hasFbq) {
        (window as any).fbq('track', 'Contact', {}, { eventID: contactEventId, ...enrichedUserData });
        (window as any).fbq('track', 'Lead', {}, { eventID: contactEventId, ...enrichedUserData });
      }

      // Proxy to backend Conversions API
      apiClient.post('facebook/event', {
        event_name: 'Contact',
        event_id: contactEventId,
        user_data: enrichedUserData,
      }).catch(() => {});

      apiClient.post('facebook/event', {
        event_name: 'Lead',
        event_id: contactEventId,
        user_data: enrichedUserData,
      }).catch(() => {});

      toast(trans(message('Your message has been submitted.')));
      navigate('/');
    },
    onError: err => onFormQueryError(err, form),
  });
}

function submitContactForm(payload: ContactPagePayload): Promise<Response> {
  return apiClient.post('contact-page', payload).then(r => r.data);
}
